import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PurchaseContract = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    id: '',
    purchase_contract_no: '',
    contract_date: '',
    state_of_purchase: '',
    state_code: '',
    commodity_id: '',
    commodity_full: '',
    supplier_id: '',
    broker_id: '',
    cha_cma_id: '',
    warehouse_id: '',
    total_qty_mt: '',
    currency: 'USD',
    unit_price_usd: '',
    incoterm: '',
    quantity_tolerance_pct: '',
    planned_fx_rate: '',
    markup_pct: '',
    hedge_executed: '',
    order_type: '',
    expected_shipment_start: '',
    expected_shipment_end: '',
    remarks: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [commodities, setCommodities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [chas, setChas] = useState([]);
  const [chaWarehouses, setChaWarehouses] = useState([]);
  const [loadingChaWarehouses, setLoadingChaWarehouses] = useState(false);
  const navigate = useNavigate();

  // Order type options
  const orderTypeOptions = [
    { value: 'Origin', name: 'Origin' },
    { value: 'High Sea', name: 'High Sea' },
    { value: 'Domestic', name: 'Domestic' }
  ];

  // Currency options
  const currencyOptions = [
    { value: 'USD', name: 'USD' },
    { value: 'INR', name: 'INR' }
  ];

  // Hedge Executed options
  const hedgeExecutedOptions = [
    { value: 'Yes', name: 'Yes' },
    { value: 'No', name: 'No' }
  ];

  const FIELD_LIMITS = {
    purchase_contract_no: 60,
    contract_date: 10, // yyyy-mm-dd
    state_of_purchase: 60,
    state_code: 45,
    commodity_full: 101,
    incoterm: 20,
    order_type: 10,
    expected_shipment_start: 10, // yyyy-mm-dd
    expected_shipment_end: 10, // yyyy-mm-dd
    remarks: 1000, // text, UI limit
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = [
      'purchase_contract_no', 'contract_date', 'state_of_purchase', 'state_code',
      'commodity_id', 'commodity_full', 'supplier_id',
      'total_qty_mt', 'currency', 'unit_price_usd', 'incoterm', 'quantity_tolerance_pct',
      'planned_fx_rate', 'order_type', 'expected_shipment_start', 'expected_shipment_end'
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const fetchContractData = useCallback(async () => {
    if (!isEditMode) return;
  
    setLoadingData(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contracts/purchase-contract-get/${id}`, {
        credentials: 'include'
      });
  
      if (res.ok) {
        const data = await res.json();
        const contract = data.data;
  
        const normalizeOrderType = (value) => {
          if (!value) return '';
          if (value === 'HS') return 'High Sea';
          if (value === 'DIRECT') return 'Origin';
          return value;
        };

        setFormData({
          id: contract.id || '',
          purchase_contract_no: contract.purchase_contract_no || '',
          contract_date: contract.contract_date || '',
          state_of_purchase: contract.state_of_purchase || '',
          state_code: contract.state_code || '',
          commodity_id: contract.commodity_id || '',
          commodity_full: contract.commodity_full || '',
          supplier_id: contract.supplier_id || '',
          broker_id: contract.broker_id || '',
          cha_cma_id: contract.cha_cma_id || '',
          warehouse_id: contract.warehouse_id || '',
          total_qty_mt: contract.total_qty_mt || '',
          currency: contract.currency || 'USD',
          unit_price_usd: contract.unit_price_usd || '',
          incoterm: contract.incoterm || '',
          quantity_tolerance_pct: contract.quantity_tolerance_pct || '',
          planned_fx_rate: contract.planned_fx_rate || '',
          markup_pct: contract.markup_pct || '',
          hedge_executed: contract.hedge_executed || '',
          order_type: normalizeOrderType(contract.order_type),
          expected_shipment_start: contract.expected_shipment_start || '',
          expected_shipment_end: contract.expected_shipment_end || '',
          remarks: contract.remarks || ''
        });
  
        if (contract.cha_cma_id) {
          fetchWarehousesByCha(contract.cha_cma_id);
        }
      } else {
        const errorText = await res.text();
        console.error('Fetch contract failed:', errorText);
        toast.error('Failed to fetch contract data');
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
      toast.error('Error loading contract data');
    } finally {
      setLoadingData(false);
    }
  }, [isEditMode, id]);
  
  // Fetch dropdown data using common masters-get API
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        console.log('Fetching dropdown data...');
        
        // Fetch commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        } else {
          console.error('Failed to fetch commodities:', commodityRes.status);
        }

        // Fetch suppliers
        const supplierRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-suppliers`, {
          credentials: 'include'
        });
        if (supplierRes.ok) {
          const supplierData = await supplierRes.json();
          console.log('Suppliers:', supplierData.data);
          setSuppliers(supplierData.data || []);
        } else {
          console.error('Failed to fetch suppliers:', supplierRes.status);
        }

        // Fetch brokers
        const brokerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-brokers`, {
          credentials: 'include'
        });
        if (brokerRes.ok) {
          const brokerData = await brokerRes.json();
          console.log('Brokers:', brokerData.data);
          setBrokers(brokerData.data || []);
        } else {
          console.error('Failed to fetch brokers:', brokerRes.status);
        }

        // Fetch CHAs
        const chaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cha-list`, {
          credentials: 'include'
        });
        if (chaRes.ok) {
          const chaData = await chaRes.json();
          console.log('CHAs:', chaData.data);
          setChas(chaData.data || []);
        } else {
          console.error('Failed to fetch CHAs:', chaRes.status);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch contract data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchContractData();
    }
  }, [isEditMode, fetchContractData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };

      if (fieldName === 'currency') {
        updated.planned_fx_rate = value === 'INR'
          ? '1'
          : (prev.currency === 'INR' && prev.planned_fx_rate === '1' ? '' : prev.planned_fx_rate);
      }

      return updated;
    });
    
    // If CHA is selected, fetch warehouses for that CHA
    if (fieldName === 'cha_cma_id') {
      fetchWarehousesByCha(value);
    }
  };

  useEffect(() => {
    if (formData.currency === 'INR' && formData.planned_fx_rate !== '1') {
      setFormData(prev => ({
        ...prev,
        planned_fx_rate: '1'
      }));
    }
  }, [formData.currency, formData.planned_fx_rate]);

  const handleStateChange = (stateName) => {
    const selectedState = stateList.find(state => state.name === stateName);
    setFormData(prev => ({ 
      ...prev, 
      state_of_purchase: stateName,
      state_code: selectedState ? selectedState.code : ''
    }));
  };

  const fetchWarehousesByCha = async (chaId) => {
    if (!chaId) {
      setChaWarehouses([]);
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
      return;
    }
    setLoadingChaWarehouses(true);
    try {
      console.log('Fetching warehouses for CHA ID:', chaId);
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cha?cha_cma_id=${chaId}`, { credentials: 'include' });
      const data = await res.json();
      console.log('CHA Warehouses response:', data);
      setChaWarehouses(data.data || []);
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
    } catch (err) {
      console.error('Error fetching CHA warehouses:', err);
      setChaWarehouses([]);
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
    } finally {
      setLoadingChaWarehouses(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    
    // Validation: check required fields
    const requiredFields = [
      'purchase_contract_no', 'contract_date', 'state_of_purchase', 'state_code',
      'commodity_id', 'commodity_full', 'supplier_id',
      'total_qty_mt', 'currency', 'unit_price_usd', 'incoterm', 'quantity_tolerance_pct',
      'planned_fx_rate', 'order_type', 'expected_shipment_start', 'expected_shipment_end'
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/contracts/purchase-contract-put`
        : `${API_BASE_URL}/api/contracts/purchase-contract-post`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        const message = isEditMode ? 'Purchase Contract Updated Successfully!' : 'Purchase Contract Created Successfully!';
        toast.success(message);
        
        if (!isEditMode) {
          // Reset form only for new contracts
          setFormData({
            id: '',
            purchase_contract_no: '',
            contract_date: '',
            state_of_purchase: '',
            state_code: '',
            commodity_id: '',
            commodity_full: '',
            supplier_id: '',
            broker_id: '',
            cha_cma_id: '',
            warehouse_id: '',
            total_qty_mt: '',
            currency: 'USD',
            unit_price_usd: '',
            incoterm: '',
            quantity_tolerance_pct: '',
            planned_fx_rate: '',
            markup_pct: '',
            hedge_executed: '',
            order_type: '',
            expected_shipment_start: '',
            expected_shipment_end: '',
            remarks: ''
          });
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">
          {isEditMode ? 'Edit Purchase Contract' : 'Purchase Contract Entry'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      {loadingData && isEditMode ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading contract data...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-0">
          <div className="px-4 pt-4 pb-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Contract No <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="purchase_contract_no" 
                  value={formData.purchase_contract_no} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.purchase_contract_no}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Contract Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="contract_date" 
                  value={formData.contract_date} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">State of Purchase <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={stateList}
                  value={formData.state_of_purchase}
                  onChange={handleStateChange}
                  placeholder="Select State"
                  displayKey="name"
                  valueKey="name"
                  required
                  onBlur={handleBlur}
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">State Code <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="state_code" 
                  value={formData.state_code} 
                  readOnly
                  maxLength={FIELD_LIMITS.state_code}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Commodity <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={commodities}
                  value={formData.commodity_id}
                  onChange={(value) => handleDropdownChange('commodity_id', value)}
                  placeholder="Select Commodity"
                  displayKey="name"
                  valueKey="commodity_id"
                  required
                  customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Commodity with full name <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="commodity_full" 
                  value={formData.commodity_full} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.commodity_full}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Supplier <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={suppliers}
                  value={formData.supplier_id}
                  onChange={(value) => handleDropdownChange('supplier_id', value)}
                  placeholder="Select Supplier"
                  displayKey="name"
                  valueKey="supplier_id"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Broker</label>
                <SearchableDropdown
                  options={brokers}
                  value={formData.broker_id}
                  onChange={(value) => handleDropdownChange('broker_id', value)}
                  placeholder="Select Broker"
                  displayKey="name"
                  valueKey="broker_id"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">CHA</label>
                <SearchableDropdown
                  options={chas}
                  value={formData.cha_cma_id}
                  onChange={(value) => handleDropdownChange('cha_cma_id', value)}
                  placeholder="Select CHA"
                  displayKey="name"
                  valueKey="cha_cma_id"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Warehouse/Port</label>
                <select 
                  name="warehouse_id" 
                  value={formData.warehouse_id || ''} 
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData(prev => ({ ...prev, warehouse_id: value }));
                  }} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  disabled={loadingChaWarehouses}
                >
                  <option value="">{loadingChaWarehouses ? 'Loading warehouses...' : '-- Select Warehouse --'}</option>
                  {chaWarehouses && chaWarehouses.length > 0 ? (
                    chaWarehouses.map(wh => (
                      <option key={wh.id || wh.warehouse_id || wh.port_id} value={wh.id || wh.warehouse_id || wh.port_id}>
                        {wh.name || wh.warehouse_name || wh.port_name || 'Unknown Warehouse'}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No warehouses available</option>
                  )}
                </select>
                {chaWarehouses && chaWarehouses.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {chaWarehouses.length} warehouse(s) available
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Total Qty (MT) <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.001"
                  name="total_qty_mt" 
                  value={formData.total_qty_mt} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Type of Purchase <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={orderTypeOptions}
                  value={formData.order_type}
                  onChange={(value) => handleDropdownChange('order_type', value)}
                  placeholder="Select Order Type"
                  displayKey="name"
                  valueKey="value"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Currency <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={currencyOptions}
                  value={formData.currency}
                  onChange={(value) => handleDropdownChange('currency', value)}
                  placeholder="Select Currency"
                  displayKey="name"
                  valueKey="value"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">
                  Unit Price ({formData.currency === 'USD' ? '$' : '₹'}) <span className="text-red-500">*</span>
                </label>
                <input 
                  type="number" 
                  step="0.01"
                  name="unit_price_usd" 
                  value={formData.unit_price_usd} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Incoterm <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="incoterm" 
                  value={formData.incoterm} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.incoterm}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Quantity Tolerance (%) <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.01"
                  name="quantity_tolerance_pct" 
                  value={formData.quantity_tolerance_pct} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Planned FX Rate <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.0001"
                  name="planned_fx_rate" 
                  value={formData.planned_fx_rate} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Markup (%)</label>
                <input 
                  type="number" 
                  step="0.01"
                  name="markup_pct" 
                  value={formData.markup_pct} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Hedge Executed</label>
                <SearchableDropdown
                  options={hedgeExecutedOptions}
                  value={formData.hedge_executed}
                  onChange={(value) => handleDropdownChange('hedge_executed', value)}
                  placeholder="Select Hedge Executed"
                  displayKey="name"
                  valueKey="value"
                />
              </div>

             

              <div>
                <label className="block text-xs font-medium mb-1">Expected Shipment Start Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="expected_shipment_start" 
                  value={formData.expected_shipment_start} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Expected Shipment End Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="expected_shipment_end" 
                  value={formData.expected_shipment_end} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                  required 
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-medium mb-1">Remarks</label>
              <textarea 
                name="remarks" 
                value={formData.remarks} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.remarks}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none" 
                rows={3}
              ></textarea>
            </div>
          </div>

          <div className="px-4 pt-2 pb-4">
            <button
              type="submit"
              className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
              disabled={loading || loadingData}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Purchase Contract' : 'Save Purchase Contract')}
            </button>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
          </div>
        </form>
      )}
    </div>
  );
};

export default PurchaseContract;
